/**
 * Global Error Handler
 * Catches unhandled errors and promise rejections that escape React error boundaries
 */

import { handleError } from '../services/errorHandler';
import { ErrorCategory } from '../types/errors';

class GlobalErrorHandler {
  private initialized = false;
  private errorHandler?: (event: ErrorEvent) => void;
  private rejectionHandler?: (event: PromiseRejectionEvent) => void;

  /**
   * Initialize global error handlers
   */
  init() {
    if (this.initialized) return;

    // Create bound handlers
    this.errorHandler = (event: ErrorEvent) => {
      console.error('Global unhandled error:', event.error);
      
      // Don't handle React errors here - they should be caught by error boundaries
      if (this.isReactError(event.error)) {
        return;
      }

      this.handleGlobalError(event.error, {
        feature: 'global',
        operation: 'unhandled_error',
        parameters: {
          filename: event.filename,
          lineno: event.lineno,
          colno: event.colno,
          source: 'window.error'
        }
      });
    };

    this.rejectionHandler = (event: PromiseRejectionEvent) => {
      console.error('Global unhandled promise rejection:', event.reason);
      
      this.handleGlobalError(event.reason, {
        feature: 'global',
        operation: 'unhandled_promise_rejection',
        parameters: {
          source: 'unhandledrejection'
        }
      });
      
      // Prevent the default browser behavior (logging to console)
      event.preventDefault();
    };

    // Handle unhandled JavaScript errors (including async errors)
    window.addEventListener('error', this.errorHandler);

    // Handle unhandled promise rejections
    window.addEventListener('unhandledrejection', this.rejectionHandler);

    this.initialized = true;
  }

  /**
   * Check if error is likely from React (should be handled by error boundaries)
   */
  private isReactError(error: any): boolean {
    if (!error || !error.stack) return false;
    
    const stack = error.stack.toString();
    return (
      stack.includes('at React') ||
      stack.includes('at Object.React') ||
      stack.includes('react-dom') ||
      stack.includes('scheduler') ||
      stack.includes('workLoop') ||
      stack.includes('performWork')
    );
  }

  /**
   * Handle global errors through our centralized error system
   */
  private async handleGlobalError(error: any, context: any) {
    try {
      // Convert various error types to Error objects
      let errorObj: Error;
      
      if (error instanceof Error) {
        errorObj = error;
      } else if (typeof error === 'string') {
        errorObj = new Error(error);
      } else if (error && typeof error === 'object') {
        errorObj = new Error(error.message || JSON.stringify(error));
      } else {
        errorObj = new Error(`Unknown error: ${String(error)}`);
      }

      // Use our centralized error handling system
      await handleError(errorObj, {
        ...context,
        category: ErrorCategory.UI, // Global errors are typically UI-related
      });
    } catch (handlingError) {
      // If our error handling system fails, fall back to console
      console.error('Failed to handle global error:', handlingError);
      console.error('Original error:', error);
    }
  }

  /**
   * Cleanup global error handlers
   */
  cleanup() {
    if (!this.initialized) return;

    if (this.errorHandler) {
      window.removeEventListener('error', this.errorHandler);
    }
    
    if (this.rejectionHandler) {
      window.removeEventListener('unhandledrejection', this.rejectionHandler);
    }
    
    this.errorHandler = undefined;
    this.rejectionHandler = undefined;
    this.initialized = false;
  }
}

// Create singleton instance
export const globalErrorHandler = new GlobalErrorHandler();

export default globalErrorHandler;
